import { FormGroup, FormControl } from '@angular/forms';

import { FieldType } from '../../shared/enums/field-type.enum';
import { SearchField } from '../../shared/models/search/search-field.model';
import { quoteString } from '../../shared/string.utils';

export interface VisualRule {
  field: SearchField
  operator: string
  text: string
  chips: string[]
  boolean: boolean
  number: {
    min: number
    max: number
  }
  date: {
    min: Date
    max: Date
  }
}

export interface VisualGroup {
  connective: 'and' | 'or'
  rules: VisualRule[]
}

export interface VisualQuery {
  outerConnective: 'and' | 'or',
  queryGroups: VisualGroup[],
}

export const ruleFormGroup = (rule: Partial<VisualRule> = {
  field: { name: '', type: FieldType.String, facet: false },
  number: { min: null, max: null },
  date: { min: null, max: null },
}): FormGroup => new FormGroup({
  field: new FormControl(rule.field.name),
  operator: new FormControl(rule.operator),
  text: new FormControl(rule.text || ''),
  chips: new FormControl(rule.chips || []),
  boolean: new FormControl(rule.boolean !== false), // default true
  number: new FormGroup({
    min: new FormControl(rule.number.min),
    max: new FormControl(rule.number.max),
  }),
  date: new FormGroup({
    min: new FormControl(rule.date.min),
    max: new FormControl(rule.date.max),
  }),
});

export interface SelectOption {
  value: string
  label?: string
}

const operatorOptions: {[type: string]: SelectOption[]} = {
  string: [
    { value: 'is' },
    { value: '!is', label: 'is not' },
    { value: 'contains' },
    { value: '!contains', label: 'does not contain' },
    { value: 'exists' },
  ],
  number: [
    { value: 'range' },
    { value: 'exists' },
  ],
  boolean: [
    { value: 'is' },
    { value: 'exists' },
  ],
  date: [
    { value: 'range' },
    { value: 'exists' },
  ],
};

export function getOperatorOptions(type: FieldType): SelectOption[] {
  switch (type) {
    case FieldType.Boolean:
      return operatorOptions.boolean;
    case FieldType.Byte:
    case FieldType.Double:
    case FieldType.Float:
    case FieldType.HalfFloat:
    case FieldType.Integer:
    case FieldType.Long:
    case FieldType.ScaledFloat:
    case FieldType.Short:
      return operatorOptions.number;
    case FieldType.Date:
      return operatorOptions.date;
    default:
      return operatorOptions.string;
  }
}

export function buildQueryString({ queryGroups, outerConnective = 'and' }: VisualQuery): string {
  const groupStrings = queryGroups.map(group => {
    const connective = group.connective.toUpperCase();
    const rules = group.rules.map(buildRuleString).filter(Boolean).join(` ${connective} `);
    return `(${rules})`;
  }).filter(group => group !== '()');
  if (groupStrings.length === 1) {
    return groupStrings[0].slice(1, -1); // trim the parentheses around the only group
  }
  return groupStrings.join(` ${outerConnective.toUpperCase()} `);
}

export function buildRuleString(rule: VisualRule): string {
  const { field: { name, type }, operator } = rule;
  if (!name) {
    return '';
  }
  if (operator === 'exists') {
    return rule.boolean
      ? `${name} EXISTS`
      : `NOT ${name} EXISTS`;
  }
  if (type === FieldType.Boolean) { // operator = is
    return `${name} = ${rule.boolean}`;
  }
  if (operator === 'range') {
    const [min, max] = getMinMax(rule);
    return [
      min ? `${name} >= ${min}` : null,
      max ? `${name} <= ${max}` : null,
    ].filter(Boolean).join(' AND ');
  }
  switch (operator) {
    case 'is':
      return rule.chips.length > 1
        ? `${name} IN [${rule.chips.join(', ')}]`
        : `${name} = ${rule.chips[0] || ''}`;
    case '!is':
      return rule.chips.length > 1
        ? `NOT ${name} IN [${rule.chips.join(', ')}]`
        : `${name} != ${rule.chips[0] || ''}`;
    case 'contains':
      return `${name} CONTAINS ${quoteString(rule.text)}`;
    case '!contains':
      return `NOT ${name} CONTAINS ${quoteString(rule.text)}`;
    default:
      return '';
  }
}

export function buildRuleField(cache: Map<string, SearchField>, field: string): Partial<SearchField> {
  field = field || '';
  const cachedField = cache.get(field);
  const fieldName = quoteString(field);
  return cachedField ?
    { ...cachedField, name: fieldName } :
    { type: FieldType.String, name: fieldName };
}

function getMinMax(rule: VisualRule): [string, string] {
  if (rule.field.type === FieldType.Date) {
    return [
      rule.date.min instanceof Date ? rule.date.min.toISOString() : '',
      rule.date.max instanceof Date ? rule.date.max.toISOString() : '',
    ];
  }
  return [
    typeof rule.number.min === 'number' ? String(rule.number.min) : '',
    typeof rule.number.max === 'number' ? String(rule.number.max) : '',
  ];
}
